import React, { useCallback, useMemo, useRef } from 'react';
import { Input, Row, Col, FormGroup, Button, InputGroup, InputGroupAddon } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import {
    TermForm,
    PurchasePriceSummary,
    DeferredFixedPaymentRow,
    DEFERRED_FIXED_PAYMENT_COLUMN_LABELS,
    DeferredVariablePaymentRow,
    DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS,
    DeferredVariablePaymentType,
    JvEquityRow,
    JV_EQUITY_COLUMN_LABELS,
    CorporateEquityRow,
    CORPORATE_EQUITY_COLUMN_LABELS,
    NO_VALUE,
    YES_VALUE,
} from '../../../../../../slices/marketplace/loi-form/term-form';
import { LegalIssueItem } from '../../../../../../slices/marketplace/loi-form/legal-issue-item';
import { TermFormFieldComment } from '../../../../../../slices/marketplace/loi-form/term-form-field-comment';
import { LoiTermFormFieldKeys, LoiTermFormRowFieldKey } from '../../../../../../slices/marketplace/loi-form/loi-term-form-field-keys';
import { TermsFormPriceSummaryBlock } from './TermsFormPriceSummaryBlock';
import { TermsFormDetailBlock, DetailColumnDef } from './TermsFormDetailBlock';
import { TermsFormLeaseTermsBlock } from './TermsFormLeaseTermsBlock';
import { TermsFormGeneralDeferredPaymentsBlock } from './TermsFormGeneralDeferredPaymentsBlock';
import { TermsFormNonCompetitionBlock } from './TermsFormNonCompetitionBlock';
import { TermsFormAssumptionOfMajorContractsBlock } from './TermsFormAssumptionOfMajorContractsBlock';
import { TermsFormPropertyPurchasedBlock } from './TermsFormPropertyPurchasedBlock';
import './TermsFormDisplay.scss';

const DFP_COLUMN_DEFS: DetailColumnDef[] = [
    { key: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.paymentTiming },
    { key: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.amount, type: 'currency' },
    { key: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.annualizedInterestAccrualRate, type: 'percent' },
    { key: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingent, type: 'yesno' },
    { key: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingencyDependentVariable, thStyle: { width: '165px', minWidth: '165px' } },
    { key: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue, type: 'currency' },
    { key: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.employmentContingency, type: 'yesno' },
    { key: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.tailPeriodForEmployment },
];

const DVP_COLUMN_DEFS: DetailColumnDef[] = [
    { key: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.paymentTiming },
    { key: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.type, type: 'select', selectOptions: [DeferredVariablePaymentType.Earnout, DeferredVariablePaymentType.Synthetic], thStyle: { width: '105px', minWidth: '105px' } },
    { key: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.valueAtClosing, type: 'currency' },
    { key: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calculationDependentVariable, thStyle: { width: '165px', minWidth: '165px' } },
    { key: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calcDvBaselineValue, type: 'currency' },
    { key: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calculationMultiple },
    { key: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.maximumPayment, type: 'currency' },
    { key: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.employmentContingency, type: 'yesno' },
    { key: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.tailPeriodForEmploymentContingency },
    { key: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.penaltyMultiple },
    { key: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.distributionPeriodicity },
];

const JV_COLUMN_DEFS: DetailColumnDef[] = [
    { key: JV_EQUITY_COLUMN_LABELS.valueAtClosing, type: 'currency' },
    { key: JV_EQUITY_COLUMN_LABELS.paymentTiming },
    { key: JV_EQUITY_COLUMN_LABELS.calculationDependentVariable, thStyle: { width: '165px', minWidth: '165px' } },
    { key: JV_EQUITY_COLUMN_LABELS.calcDvBaselineValue, type: 'currency' },
    { key: JV_EQUITY_COLUMN_LABELS.calculationMultiple },
    { key: JV_EQUITY_COLUMN_LABELS.employmentContingency, type: 'yesno' },
    { key: JV_EQUITY_COLUMN_LABELS.tailPeriodForEmploymentContingency },
    { key: JV_EQUITY_COLUMN_LABELS.penaltyMultiple },
    { key: JV_EQUITY_COLUMN_LABELS.distributionPeriodicity },
];

const CORP_COLUMN_DEFS: DetailColumnDef[] = [
    { key: CORPORATE_EQUITY_COLUMN_LABELS.valueAtClosing, type: 'currency' },
    { key: CORPORATE_EQUITY_COLUMN_LABELS.expectedTimingToRecap },
    { key: CORPORATE_EQUITY_COLUMN_LABELS.expectedPayoutAsMultipleOfCurrentValue },
];

export interface TermsFormDisplayProps {
    termForm: TermForm;
    legalIssues?: LegalIssueItem[];
    additionalCovenants?: LegalIssueItem[];
    readOnly?: boolean;
    onChange?: (form: TermForm) => void;
    errors?: Set<string>;
    isFsb?: boolean;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    fieldComments?: TermFormFieldComment[];
    onFieldCommentChange?: (fieldKey: string, comment: string | null) => void;
}


function totalDfp(rows: DeferredFixedPaymentRow[]): number {
    return rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
}
function totalDvp(rows: DeferredVariablePaymentRow[]): number {
    return rows.reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);
}
function summaryTotal(summary: PurchasePriceSummary | null | undefined): number {
    if (!summary) return 0;
    const cash = Number(summary.cashAtCloser?.cashAtClose) || 0;
    const dfp = totalDfp(summary.deferredFixedPayments?.rows ?? []);
    const dvp = totalDvp(summary.deferredVariablePayments?.rows ?? []);
    const jv = Number(summary.jvEquity?.row?.valueAtClosing) || 0;
    const corp = Number(summary.corporateEquity?.row?.valueAtClosing) || 0;
    const other = Number(summary.other) || 0;
    return cash + dfp + dvp + jv + corp + other;
}

function rowToKeyValue(row: DeferredFixedPaymentRow): Record<string, string | null> {
    return {
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.paymentTiming]: row.paymentTiming ?? null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.amount]:
            row.amount != null ? String(row.amount) : null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.annualizedInterestAccrualRate]:
            row.annualizedInterestAccrualRate ?? null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingent]: row.contingent ?? null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingencyDependentVariable]:
            row.contingencyDependentVariableValue ?? null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue]:
            row.contDvMinimumValue != null ? String(row.contDvMinimumValue) : null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.employmentContingency]:
            row.employmentContingency ?? null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.tailPeriodForEmployment]:
            row.tailPeriodForEmployment ?? null,
    };
}
function rowFromKeyValue(kv: Record<string, string | null>, base?: DeferredFixedPaymentRow): DeferredFixedPaymentRow {
    return {
        considerationForEmployees: base?.considerationForEmployees ?? null,
        netToSeller: base?.netToSeller ?? null,
        fairValue: base?.fairValue ?? null,
        fairValueExplanation: base?.fairValueExplanation ?? null,
        paymentTiming: kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.paymentTiming] ?? null,
        amount:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.amount] != null &&
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.amount] !== ''
                ? Number(kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.amount])
                : null,
        annualizedInterestAccrualRate:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.annualizedInterestAccrualRate] ?? null,
        contingent: kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingent] ?? null,
        contingencyDependentVariableValue:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingencyDependentVariable] ?? null,
        contDvMinimumValue:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue] != null &&
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue] !== ''
                ? Number(kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue])
                : null,
        employmentContingency:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.employmentContingency] ?? null,
        tailPeriodForEmployment:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.tailPeriodForEmployment] ?? null,
    };
}
function dvpRowToKeyValue(row: DeferredVariablePaymentRow): Record<string, string | null> {
    return {
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.paymentTiming]: row.paymentTiming ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.type]:
            row.type != null ? String(row.type) : null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.valueAtClosing]:
            row.valueAtClosing != null ? String(row.valueAtClosing) : null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calculationDependentVariable]:
            row.calculationDependentVariable ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calcDvBaselineValue]:
            row.calcDvBaselineValue != null ? String(row.calcDvBaselineValue) : null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calculationMultiple]:
            row.calculationMultiple ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.maximumPayment]:
            row.maximumPayment ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.employmentContingency]:
            row.employmentContingency ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.tailPeriodForEmploymentContingency]:
            row.tailPeriodForEmploymentContingency ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.penaltyMultiple]:
            row.penaltyMultiple ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.distributionPeriodicity]:
            row.distributionPeriodicity ?? null,
    };
}
function dvpRowFromKeyValue(kv: Record<string, string | null>, base?: DeferredVariablePaymentRow): DeferredVariablePaymentRow {
    const typeVal = kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.type];
    let type: DeferredVariablePaymentType | null = null;
    if (typeVal === DeferredVariablePaymentType.Earnout || typeVal === DeferredVariablePaymentType.Synthetic) {
        type = typeVal;
    } else if (typeVal?.toLowerCase().includes('synthetic')) {
        type = DeferredVariablePaymentType.Synthetic;
    } else if (typeVal?.toLowerCase() === 'earnout') {
        type = DeferredVariablePaymentType.Earnout;
    }
    return {
        considerationForEmployees: base?.considerationForEmployees ?? null,
        netToSeller: base?.netToSeller ?? null,
        fairValue: base?.fairValue ?? null,
        fairValueExplanation: base?.fairValueExplanation ?? null,
        paymentTiming: kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.paymentTiming] ?? null,
        type,
        valueAtClosing:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.valueAtClosing] != null &&
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.valueAtClosing] !== ''
                ? Number(kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.valueAtClosing])
                : null,
        calculationDependentVariable:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calculationDependentVariable] ?? null,
        calcDvBaselineValue:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calcDvBaselineValue] != null &&
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calcDvBaselineValue] !== ''
                ? Number(kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calcDvBaselineValue])
                : null,
        calculationMultiple:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calculationMultiple] ?? null,
        maximumPayment: kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.maximumPayment] ?? null,
        employmentContingency:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.employmentContingency] ?? null,
        tailPeriodForEmploymentContingency:
            kv[
                DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.tailPeriodForEmploymentContingency
            ] ?? null,
        penaltyMultiple: kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.penaltyMultiple] ?? null,
        distributionPeriodicity:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.distributionPeriodicity] ?? null,
    };
}

export const TermsFormDisplay = (props: TermsFormDisplayProps) => {
    const { termForm, legalIssues = [], additionalCovenants = [], readOnly = false, onChange, errors, isFsb, isBuyerReview, isBuyerReviewConfirmed, fieldComments, onFieldCommentChange } = props;
    const summary = termForm.purchasePriceSummary;

    const getComment = (fieldKey: string) =>
        fieldComments?.find(c => c.fieldKey === fieldKey)?.comment ?? null;

    const showComments = isBuyerReview || !!isBuyerReviewConfirmed;

    const commentCell = (fieldKey: string) => {
        if (!showComments) return null;
        const comment = getComment(fieldKey);
        return (
            <td className="terms-form-col-comment">
                {(isBuyerReview || comment) && (
                    <Input
                        type="textarea"
                        value={comment ?? ''}
                        readOnly={!isBuyerReview}
                        onChange={(e) =>
                            onFieldCommentChange?.(fieldKey, e.target.value === '' ? null : e.target.value)
                        }
                        className="terms-form-comment-input"
                        aria-label={`Comment for ${fieldKey}`}
                    />
                )}
            </td>
        );
    };
    const total = summaryTotal(summary);
    const loiDatePickerRef = useRef<any>(null);
    const showNonCompetition = useMemo(
        () => termForm.locations?.some(l => l.leaseAssumption === 'new') ?? false,
        [termForm.locations]
    );

    const update = useCallback(
        (patch: Partial<TermForm>) => {
            onChange?.({ ...termForm, ...patch });
        },
        [termForm, onChange]
    );

    const updateSummary = useCallback(
        (patch: Partial<PurchasePriceSummary>) => {
            onChange?.({
                ...termForm,
                purchasePriceSummary: {
                    ...(termForm.purchasePriceSummary ?? {}),
                    ...patch,
                },
            });
        },
        [termForm, onChange]
    );

    const setDfpRows = useCallback(
        (rows: DeferredFixedPaymentRow[]) => {
            updateSummary({ deferredFixedPayments: { rows } });
        },
        [updateSummary]
    );
    const setDvpRows = useCallback(
        (rows: DeferredVariablePaymentRow[]) => {
            updateSummary({ deferredVariablePayments: { rows } });
        },
        [updateSummary]
    );
    const setJvRow = useCallback(
        (row: JvEquityRow | null) => {
            updateSummary({ jvEquity: { row } });
        },
        [updateSummary]
    );
    const setCorpRow = useCallback(
        (row: CorporateEquityRow | null) => {
            updateSummary({ corporateEquity: { row } });
        },
        [updateSummary]
    );

    const dfpRows = summary?.deferredFixedPayments?.rows ?? [];
    const dvpRows = summary?.deferredVariablePayments?.rows ?? [];
    const hasDfpValue = dfpRows.length > 0 && totalDfp(dfpRows) > 0;
    const hasDvpValue = dvpRows.length > 0 && totalDvp(dvpRows) > 0;
    const jvRow = summary?.jvEquity?.row ?? null;
    const corpRow = summary?.corporateEquity?.row ?? null;
    const jvVal = summary?.jvEquity?.row?.valueAtClosing;
    const hasJvValue = jvVal != null && Number(jvVal) !== 0;
    const corpVal = summary?.corporateEquity?.row?.valueAtClosing;
    const hasCorpValue = corpVal != null && Number(corpVal) !== 0;

    const dfpRowComments = dfpRows.map((_, i) =>
        fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.dfpRow(i))?.comment ?? null
    );
    const handleDfpRowCommentChange = (rowIndex: number, comment: string | null) =>
        onFieldCommentChange?.(LoiTermFormRowFieldKey.dfpRow(rowIndex), comment);

    const jvRowComments = [fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.jvEquityRow(0))?.comment ?? null];
    const handleJvRowCommentChange = (rowIndex: number, comment: string | null) =>
        onFieldCommentChange?.(LoiTermFormRowFieldKey.jvEquityRow(rowIndex), comment);

    const corpRowComments = [fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.corporateEquityRow(0))?.comment ?? null];
    const handleCorpRowCommentChange = (rowIndex: number, comment: string | null) =>
        onFieldCommentChange?.(LoiTermFormRowFieldKey.corporateEquityRow(rowIndex), comment);

    const earnoutSum = dvpRows
        .filter((r) => r.type === DeferredVariablePaymentType.Earnout)
        .reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);
    const syntheticJvSum = dvpRows
        .filter((r) => r.type === DeferredVariablePaymentType.Synthetic)
        .reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);

    const dfpInvalidCells = useMemo(() => {
        if (!errors) return undefined;
        const cells = new Set<string>();
        Array.from(errors).forEach((key) => {
            if (key.startsWith('dfp:')) cells.add(key.substring(4));
        });
        return cells.size > 0 ? cells : undefined;
    }, [errors]);

    const dvpInvalidCells = useMemo(() => {
        if (!errors) return undefined;
        const cells = new Set<string>();
        Array.from(errors).forEach((key) => {
            if (key.startsWith('dvp:')) cells.add(key.substring(4));
        });
        return cells.size > 0 ? cells : undefined;
    }, [errors]);

    const jvInvalidCells = useMemo(() => {
        if (!errors) return undefined;
        const cells = new Set<string>();
        Array.from(errors).forEach((key) => {
            if (key.startsWith('jv:')) cells.add(key.substring(3));
        });
        return cells.size > 0 ? cells : undefined;
    }, [errors]);

    const corpInvalidCells = useMemo(() => {
        if (!errors) return undefined;
        const cells = new Set<string>();
        Array.from(errors).forEach((key) => {
            if (key.startsWith('corp:')) cells.add(key.substring(5));
        });
        return cells.size > 0 ? cells : undefined;
    }, [errors]);

    return (
        <div className="terms-form-display">
            <div className="terms-form-section">
                <div className="terms-form-section-header">LOI Terms Input Form</div>
                <div className="terms-form-section-body">
                    <div className="terms-form-detail-block terms-form-section-underlined">
                        <div className="terms-period-table terms-form-price-summary-table-container">
                            <table className="terms-form-price-summary-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '195px', minWidth: '195px' }}/>
                                        <th className="terms-form-col-value" style={{ width: '155px', minWidth: '155px' }}/>
                                        <th className="terms-form-col-comment">{showComments && "Comments"}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="terms-form-col-item table-label-cell">LOI Expiration Date</td>
                                        <td>
                                            {readOnly ? (
                                                <Input
                                                    type="text"
                                                    value={
                                                        termForm.loiExpirationDate != null
                                                            ? termForm.loiExpirationDate.split('T')[0]
                                                            : ''
                                                    }
                                                    disabled
                                                    className="expiration-date-input form-control loi-form-input expiration-date-input"
                                                    aria-label="LOI Expiration Date"
                                                />
                                            ) : (
                                                <div
                                                    className="terms-form-date-picker-wrap"
                                                    onClick={() =>
                                                        loiDatePickerRef.current?.setOpen?.(true)
                                                    }>
                                                    <div className={`d-flex small-border expiration-date-input ${errors?.has('loiExpirationDate') ? ' is-invalid' : ''}`}>
                                                        <InputGroupAddon
                                                            addonType="prepend"
                                                            onClick={() =>
                                                                loiDatePickerRef.current?.setOpen?.(true)
                                                            }>
                                                            <div className="input-group-text">
                                                                <FontAwesomeIcon icon={faCalendarAlt} />
                                                            </div>
                                                        </InputGroupAddon>
                                                        <DatePicker
                                                            ref={loiDatePickerRef}
                                                            selected={
                                                                termForm.loiExpirationDate
                                                                    ? new Date(termForm.loiExpirationDate)
                                                                    : null
                                                            }
                                                            onChange={(date: Date | null) =>
                                                                update({
                                                                    loiExpirationDate: date
                                                                        ? date.toISOString().slice(0, 10)
                                                                        : null,
                                                                })
                                                            }
                                                            dateFormat="MM/dd/yyyy"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                        {commentCell(LoiTermFormFieldKeys.loiExpirationDate)}
                                    </tr>
                                    <tr>
                                        <td className="terms-form-col-item table-label-cell">Exclusivity Period</td>
                                        <td className="terms-form-col-value">
                                            <div className="terms-form-col-value-with-unit">
                                                <Input
                                                    type="number"
                                                    value={termForm.exclusivityPeriodMonth ?? ''}
                                                    disabled={readOnly}
                                                    onChange={(e) =>
                                                        update({
                                                            exclusivityPeriodMonth:
                                                                e.target.value === ''
                                                                    ? null
                                                                    : Number(e.target.value),
                                                        })
                                                    }
                                                    invalid={errors?.has('exclusivityPeriodMonth')}
                                                    className="form-control loi-form-input"
                                                    aria-label="Exclusivity Period"
                                                />
                                                <span className="terms-form-unit-label">Months</span>
                                            </div>
                                        </td>
                                        {commentCell(LoiTermFormFieldKeys.exclusivityPeriodMonth)}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <TermsFormPropertyPurchasedBlock
                        termForm={termForm}
                        readOnly={readOnly}
                        onChange={onChange}
                        errors={errors}
                        isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                        fieldComments={fieldComments}
                        onFieldCommentChange={onFieldCommentChange}
                    />

                    <TermsFormPriceSummaryBlock
                        summary={summary}
                        total={total}
                        onSummaryChange={updateSummary}
                        earnoutSum={earnoutSum}
                        syntheticJvSum={syntheticJvSum}
                        readOnly={readOnly}
                        errors={errors}
                        isEmployeeConsiderationPresent={termForm.isEmployeeConsiderationPresent}
                        isFsb={isFsb}
                        isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                        fieldComments={fieldComments}
                        onFieldCommentChange={onFieldCommentChange}
                    />

                    {(hasDfpValue || hasDvpValue) && (
                        <TermsFormGeneralDeferredPaymentsBlock
                            termForm={termForm}
                            readOnly={readOnly}
                            onChange={onChange}
                            errors={errors}
                            isBuyerReview={isBuyerReview}
                            isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                            fieldComments={fieldComments}
                            onFieldCommentChange={onFieldCommentChange}
                        />
                    )}

                    {hasDfpValue && (
                        <TermsFormDetailBlock
                            className="terms-form-detail-block--deferred-fixed-payments"
                            hideLastRowBorder
                            title="Deferred Fixed Payments"
                            columns={DFP_COLUMN_DEFS}
                            rows={dfpRows.map(rowToKeyValue)}
                            readOnly={readOnly}
                            onRowsChange={(rowsKv) =>
                                setDfpRows(rowsKv.map((kv, i) => rowFromKeyValue(kv, dfpRows[i])))
                            }
                            displayTotal={totalDfp(dfpRows)}
                            showTotalRow
                            onAddRow={() =>
                                setDfpRows([
                                    ...dfpRows,
                                    {
                                        paymentTiming: null,
                                        amount: null,
                                        annualizedInterestAccrualRate: null,
                                        contingent: NO_VALUE,
                                        contingencyDependentVariableValue: null,
                                        contDvMinimumValue: null,
                                        employmentContingency: NO_VALUE,
                                        tailPeriodForEmployment: null,
                                    },
                                ])
                            }
                            onRemoveRow={(i) => setDfpRows(dfpRows.filter((_, idx) => idx !== i))}
                            cellDisablingRules={[
                                {
                                    column: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingencyDependentVariable,
                                    dependsOnColumn: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingent,
                                    enabledWhen: YES_VALUE,
                                },
                                {
                                    column: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue,
                                    dependsOnColumn: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingent,
                                    enabledWhen: YES_VALUE,
                                },
                                {
                                    column: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.tailPeriodForEmployment,
                                    dependsOnColumn: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.employmentContingency,
                                    enabledWhen: YES_VALUE,
                                },
                            ]}
                            invalidCells={dfpInvalidCells}
                            isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                            rowComments={dfpRowComments}
                            onRowCommentChange={handleDfpRowCommentChange}
                        />
                    )}

                    {hasDvpValue && (
                        <TermsFormDetailBlock
                            className="terms-form-detail-block--deferred-variable-payments"
                            hideLastRowBorder
                            title="Deferred Variable Payments"
                            columns={DVP_COLUMN_DEFS}
                            rows={dvpRows.map(dvpRowToKeyValue)}
                            readOnly={readOnly}
                            onRowsChange={(rowsKv) =>
                                setDvpRows(rowsKv.map((kv, i) => dvpRowFromKeyValue(kv, dvpRows[i])))
                            }
                            onAddRow={() =>
                                setDvpRows([
                                    ...dvpRows,
                                    {
                                        paymentTiming: null,
                                        type: null,
                                        valueAtClosing: null,
                                        calculationDependentVariable: null,
                                        calcDvBaselineValue: null,
                                        calculationMultiple: null,
                                        maximumPayment: null,
                                        employmentContingency: NO_VALUE,
                                        tailPeriodForEmploymentContingency: null,
                                        penaltyMultiple: null,
                                        distributionPeriodicity: null,
                                    },
                                ])
                            }
                            onRemoveRow={(i) => setDvpRows(dvpRows.filter((_, idx) => idx !== i))}
                            showPaymentNumber
                            paymentNumberHeader=""
                            cellDisablingRules={[
                                {
                                    column: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.tailPeriodForEmploymentContingency,
                                    dependsOnColumn: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.employmentContingency,
                                    enabledWhen: YES_VALUE,
                                },
                            ]}
                            invalidCells={dvpInvalidCells}
                            isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                            rowComments={dvpRows.map((_, i) =>
                                fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.dvpRow(i))?.comment ?? null
                            )}
                            onRowCommentChange={(i, c) => onFieldCommentChange?.(LoiTermFormRowFieldKey.dvpRow(i), c)}
                        />
                    )}

                    {hasJvValue && (
                        <TermsFormDetailBlock
                            className="terms-form-detail-block--jv-equity"
                            title="JV Equity"
                            columns={JV_COLUMN_DEFS}
                            rows={[
                                {
                                    [JV_EQUITY_COLUMN_LABELS.valueAtClosing]:
                                        jvRow?.valueAtClosing != null
                                            ? String(jvRow.valueAtClosing)
                                            : (jvVal != null ? String(jvVal) : null),
                                    [JV_EQUITY_COLUMN_LABELS.paymentTiming]:
                                        jvRow?.paymentTiming ?? null,
                                    [JV_EQUITY_COLUMN_LABELS.calculationDependentVariable]:
                                        jvRow?.calculationDependentVariable ?? null,
                                    [JV_EQUITY_COLUMN_LABELS.calcDvBaselineValue]:
                                        jvRow?.calcDvBaselineValue != null
                                            ? String(jvRow.calcDvBaselineValue)
                                            : null,
                                    [JV_EQUITY_COLUMN_LABELS.calculationMultiple]:
                                        jvRow?.calculationMultiple ?? null,
                                    [JV_EQUITY_COLUMN_LABELS.employmentContingency]:
                                        jvRow?.employmentContingency ?? null,
                                    [JV_EQUITY_COLUMN_LABELS.tailPeriodForEmploymentContingency]:
                                        jvRow?.tailPeriodForEmploymentContingency ?? null,
                                    [JV_EQUITY_COLUMN_LABELS.penaltyMultiple]:
                                        jvRow?.penaltyMultiple ?? null,
                                    [JV_EQUITY_COLUMN_LABELS.distributionPeriodicity]:
                                        jvRow?.distributionPeriodicity ?? null,
                                },
                            ]}
                            readOnly={readOnly}
                            onRowsChange={(rowsKv) =>
                                setJvRow(
                                    rowsKv.length > 0
                                        ? {
                                              considerationForEmployees: jvRow?.considerationForEmployees ?? null,
                                              netToSeller: jvRow?.netToSeller ?? null,
                                              fairValue: jvRow?.fairValue ?? null,
                                              fairValueExplanation: jvRow?.fairValueExplanation ?? null,
                                              valueAtClosing:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.valueAtClosing] != null &&
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.valueAtClosing] !== ''
                                                      ? Number(rowsKv[0][JV_EQUITY_COLUMN_LABELS.valueAtClosing])
                                                      : null,
                                              paymentTiming:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.paymentTiming] ?? null,
                                              calculationDependentVariable:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.calculationDependentVariable] ??
                                                  null,
                                              calcDvBaselineValue:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.calcDvBaselineValue] != null &&
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.calcDvBaselineValue] !== ''
                                                      ? Number(rowsKv[0][JV_EQUITY_COLUMN_LABELS.calcDvBaselineValue])
                                                      : null,
                                              calculationMultiple:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.calculationMultiple] ?? null,
                                              employmentContingency:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.employmentContingency] ?? null,
                                              tailPeriodForEmploymentContingency:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.tailPeriodForEmploymentContingency] ??
                                                  null,
                                              penaltyMultiple:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.penaltyMultiple] ?? null,
                                              distributionPeriodicity:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.distributionPeriodicity] ?? null,
                                            }
                                        : null
                                )
                            }
                            singleRow
                            hideAddRow
                            showRowBorder={false}
                            cellDisablingRules={[
                                {
                                    column: JV_EQUITY_COLUMN_LABELS.tailPeriodForEmploymentContingency,
                                    dependsOnColumn: JV_EQUITY_COLUMN_LABELS.employmentContingency,
                                    enabledWhen: YES_VALUE,
                                },
                            ]}
                            invalidCells={jvInvalidCells}
                            isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                            rowComments={jvRowComments}
                            onRowCommentChange={handleJvRowCommentChange}
                        />
                    )}

                    {hasCorpValue && (
                        <TermsFormDetailBlock
                            className="terms-form-detail-block--corporate-equity"
                            title="Corporate Equity"
                            columns={CORP_COLUMN_DEFS}
                            rows={[
                                {
                                    [CORPORATE_EQUITY_COLUMN_LABELS.valueAtClosing]:
                                        corpRow?.valueAtClosing != null
                                            ? String(corpRow.valueAtClosing)
                                            : (corpVal != null ? String(corpVal) : null),
                                    [CORPORATE_EQUITY_COLUMN_LABELS.expectedTimingToRecap]:
                                        corpRow?.expectedTimingToRecap ?? null,
                                    [CORPORATE_EQUITY_COLUMN_LABELS.expectedPayoutAsMultipleOfCurrentValue]:
                                        corpRow?.expectedPayoutAsMultipleOfCurrentValue ?? null,
                                },
                            ]}
                            readOnly={readOnly}
                            onRowsChange={(rowsKv) =>
                                setCorpRow(
                                    rowsKv.length > 0
                                        ? {
                                              considerationForEmployees: corpRow?.considerationForEmployees ?? null,
                                              netToSeller: corpRow?.netToSeller ?? null,
                                              fairValue: corpRow?.fairValue ?? null,
                                              fairValueExplanation: corpRow?.fairValueExplanation ?? null,
                                              valueAtClosing:
                                                  rowsKv[0][CORPORATE_EQUITY_COLUMN_LABELS.valueAtClosing] != null &&
                                                  rowsKv[0][CORPORATE_EQUITY_COLUMN_LABELS.valueAtClosing] !== ''
                                                      ? Number(rowsKv[0][CORPORATE_EQUITY_COLUMN_LABELS.valueAtClosing])
                                                      : null,
                                              expectedTimingToRecap:
                                                  rowsKv[0][CORPORATE_EQUITY_COLUMN_LABELS.expectedTimingToRecap] ?? null,
                                              expectedPayoutAsMultipleOfCurrentValue:
                                                  rowsKv[0][CORPORATE_EQUITY_COLUMN_LABELS.expectedPayoutAsMultipleOfCurrentValue] ?? null,
                                          }
                                        : null
                                )
                            }
                            singleRow
                            showRowBorder={false}
                            invalidCells={corpInvalidCells}
                            isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                            rowComments={corpRowComments}
                            onRowCommentChange={handleCorpRowCommentChange}
                        />
                    )}

                    <TermsFormLeaseTermsBlock
                        termForm={termForm}
                        readOnly={readOnly}
                        onChange={onChange}
                        errors={errors}
                        isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                        rowComments={
                            (termForm.locations ?? [])
                                .filter((l) => l.leaseAssumption === 'new')
                                .map((l) =>
                                    fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.leaseRow(l.locationId))?.comment ?? null
                                )
                        }
                        onRowCommentChange={(i, c) => {
                            const locationId = (termForm.locations ?? []).filter(l => l.leaseAssumption === 'new')[i]?.locationId;
                            if (locationId) onFieldCommentChange?.(LoiTermFormRowFieldKey.leaseRow(locationId), c);
                        }}
                    />

                    {showNonCompetition && (
                        <TermsFormNonCompetitionBlock
                            legalIssues={legalIssues}
                            additionalCovenants={additionalCovenants}
                            readOnly={readOnly}
                            isBuyerReview={isBuyerReview}
                            isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                            rowComments={[fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.ncrRow(0))?.comment ?? null]}
                            onRowCommentChange={(_, c) => onFieldCommentChange?.(LoiTermFormRowFieldKey.ncrRow(0), c)}
                        />
                    )}

                    <TermsFormAssumptionOfMajorContractsBlock
                        termForm={termForm}
                        readOnly={readOnly}
                        onChange={onChange}
                        errors={errors}
                        isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                        rowComments={[fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.amcRow(0))?.comment ?? null]}
                        onRowCommentChange={(_, c) => onFieldCommentChange?.(LoiTermFormRowFieldKey.amcRow(0), c)}
                    />
                </div>
            </div>
        </div>
    );
};
